import React, { createContext, useContext, useMemo, useState, useCallback } from "react";

/**
 * Toolbar coordination via context.
 * Mid-tab (PageToolbar) → Form toolbars → Field tools can talk to each
 * other through this bus without prop drilling.
 *
 * Example:
 *   <ToolbarBusProvider>
 *     <PageToolbar>
 *       <ToolbarButton icon="bi-save" onClick={() => bus.emit('save')} />
 *     </PageToolbar>
 *     <MyForm />   // internally: bus.on('save', handleSave)
 *   </ToolbarBusProvider>
 */
const ToolbarBusContext = createContext(null);

export const ToolbarBusProvider = ({ children }) => {
  const listenersRef = React.useRef({});

  const on = useCallback((event, fn) => {
    listenersRef.current[event] ??= new Set();
    listenersRef.current[event].add(fn);
    return () => listenersRef.current[event]?.delete(fn);
  }, []);

  const emit = useCallback((event, payload) => {
    listenersRef.current[event]?.forEach((fn) => fn(payload));
  }, []);

  const value = useMemo(() => ({ on, emit }), [on, emit]);
  return <ToolbarBusContext.Provider value={value}>{children}</ToolbarBusContext.Provider>;
};

export const useToolbarBus = () => {
  const ctx = useContext(ToolbarBusContext);
  if (!ctx) {
    // Safe no-op fallback so components still work without a provider
    return { on: () => () => {}, emit: () => {} };
  }
  return ctx;
};

/**
 * PageToolbar — the mid-tab toolbar.
 * Sits directly below PageHeader. Holds page-level tools that may
 * trigger actions inside child forms (via ToolbarBus events) or act
 * globally (refresh, filters, view settings).
 *
 * Props:
 *   left   — React node for the left slot (typically primary tools)
 *   right  — React node for the right slot (typically page-wide actions)
 *   sticky — whether to stick to viewport top when scrolling
 *   children — if you don't want slots, children go between left and right
 */
const PageToolbar = ({ left, right, sticky = false, children, className = "" }) => {
  return (
    <div
      className={`ds-page-toolbar ${sticky ? "ds-form-toolbar--sticky" : ""} ${className}`}
      role="toolbar"
      aria-label="Page tools"
    >
      {left && <div className="d-flex align-items-center gap-2 flex-wrap">{left}</div>}
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
      {right && <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">{right}</div>}
    </div>
  );
};

/** Logical grouping of tools inside any toolbar. Renders as a pill. */
export const ToolbarGroup = ({ label, children, className = "" }) => (
  <div className={`ds-page-toolbar__group ${className}`} role="group" aria-label={label}>
    {label && <span className="ds-page-toolbar__label">{label}</span>}
    {children}
  </div>
);

export default PageToolbar;
